import type { SearchParameters } from 'ofetch'
import type { AvailableRouterMethod, NitroFetchOptions, NitroFetchRequest } from 'nitropack'
import {
  addTraceHeaders,
  handleError,
  handleRequest,
  handleRequestError,
  handleResponse,
  resolveSsrApiUrl,
  type ResOptions
} from '~/composables/httpHelper'

const fetch = async <R extends NitroFetchRequest, M extends AvailableRouterMethod<R>, T = any>(
  url: string,
  opts: NitroFetchOptions<R, M>
) => {
  // 在请求开始时捕获实例；响应、重试回调可能在原调用的上下文结束后执行。
  const nuxtApp = useNuxtApp()
  const meta: Record<string, any> = {}
  const { public: env } = useRuntimeConfig()
  const isOssApi = url.includes('ossApi')
  // 客户端走 API_BASE_URL；SSR 的 /api 直连 BASE_URL（与 Nitro proxy 路径一致），避免自代理回环
  let requestUrl = url
  let baseUrl = ''
  if (process.client && !isOssApi) {
    baseUrl = env.config.API_BASE_URL
  } else if (process.server && !isOssApi && url.startsWith('/api/')) {
    requestUrl = resolveSsrApiUrl(url, env.config.BASE_URL)
  }

  const routeMode = process.client
    ? 'client'
    : isOssApi
      ? 'ssr-oss-proxy'
      : url.startsWith('/api/')
        ? requestUrl !== url
          ? 'ssr-direct'
          : 'ssr-nitro-proxy'
        : 'ssr-other'

  const token = useCookie<string | undefined>('access_token')
  const { $i18n } = nuxtApp

  const locale = $i18n.locale.value as keyof typeof ELocale

  // 🔧 根据环境设置默认超时
  const isSSR = process.server
  const defaultTimeout = isSSR ? 6000 : 10000 // SSR 6秒，客户端 10秒
  const timeoutMs = (opts.timeout as number | undefined) || defaultTimeout

  meta.logicalUrl = url
  meta.resolvedUrl = baseUrl ? `${baseUrl}${requestUrl}` : requestUrl
  meta.routeMode = routeMode
  meta.timeoutMs = timeoutMs
  if (import.meta.server) {
    try {
      meta.pageUrl = useRequestEvent()?.node.req.url
    } catch {
      /* island 等场景可能无 event */
    }
    meta.pid = process.pid
  }

  return $fetch<ResOptions<T>>(requestUrl, {
    baseURL: baseUrl,

    // 🔧 关键修改：添加超时配置
    timeout: timeoutMs,

    // 🔧 优化重试策略：SSR 只重试 1 次
    retry: isSSR ? 1 : 2,
    retryDelay: 300, // 减少重试延迟

    // 🔧 重试条件：只在特定状态码时重试，避免超时重试
    retryStatusCodes: [408, 425, 429, 500, 502, 503, 504],

    // 请求拦截：先加 trace 头（前后端链路关联），再走通用 header/body
    onRequest(ctx) {
      return nuxtApp.runWithContext(() => {
        addTraceHeaders(ctx)
        handleRequest(meta, token, locale, ctx, opts)
      })
    },
    // 请求错误拦截
    onRequestError(ctx) {
      return nuxtApp.runWithContext(() => {
        handleRequestError(ctx, token, meta)
      })
    },
    // 响应拦截
    onResponse(ctx) {
      return nuxtApp.runWithContext(() => {
        handleResponse(meta, token, ctx)
      })
    },
    // 错误拦截
    onResponseError(ctx) {
      return nuxtApp.runWithContext(() => {
        handleError(ctx, token, meta)
      })
    },
    // 合并 options
    ...opts
  })
}

export const useHttp = {
  get: <R extends NitroFetchRequest, M extends AvailableRouterMethod<R>>(
    url: string,
    params?: SearchParameters,
    option?: NitroFetchOptions<R, M>
  ) => {
    return fetch(url, { method: 'GET', params, ...option }) // .then(res => res.data.value);
  },

  post: <R extends NitroFetchRequest, M extends AvailableRouterMethod<R>>(
    url: string,
    body?: RequestInit['body'] | Record<string, any>,
    option?: NitroFetchOptions<R, M>
  ) => {
    //   console.log('40-40', {...option})
    return fetch(url, { method: 'POST', body, ...option }) // .then(res => res.data.value);
  },

  put: <R extends NitroFetchRequest, M extends AvailableRouterMethod<R>>(
    url: string,
    body?: RequestInit['body'] | Record<string, any>,
    option?: NitroFetchOptions<R, M>
  ) => {
    return fetch(url, { method: 'PUT', body, ...option }) // .then(res => res.data.value);
  },

  delete: <R extends NitroFetchRequest, M extends AvailableRouterMethod<R>>(
    url: string,
    body?: RequestInit['body'] | Record<string, any>,
    option?: NitroFetchOptions<R, M>
  ) => {
    return fetch(url, { method: 'DELETE', body, ...option }) // .then(res => res.data.value);
  }
}
